import { Carousel } from '@strata/react-carousel';
import { FileUploadFormik } from '@strata/react-file-upload';
import { InlineMessage } from '@strata/react-inline-message';
import { TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { HomeScreen } from '@strata/react-page-templates';
import { validationStrings } from '@strata/strata-ui-strings';
import { saveAs } from 'file-saver';
import * as Yup from 'yup';

import { CardArray } from 'pages/homeScreenMessaging/CardArray';
import {
    ApplicationInputs,
    DisplayInput,
    EndDateInput,
    MessageCategoryInput,
    PublishDateInput,
} from 'pages/homeScreenMessaging/HomeScreenFormInputs';
import { HomeScreenMessageForm } from 'pages/homeScreenMessaging/HomeScreenMessageForm';

const formContent = (formik) => (
    <>
        <MessageCategoryInput label="Feature Name" />
        <ApplicationInputs />
        <PublishDateInput messageType={HomeScreen.MessageType.CAROUSEL} />
        <EndDateInput messageType={HomeScreen.MessageType.CAROUSEL} />
        <DisplayInput />
        <div className="padding__bottom--large">
            <InlineMessage
                intent={InlineMessage.Intent.INFO}
                wrapText
                label="The feature carousel will be presented to each user upon login. Once viewed, it will not be shown on subsequent logins."
            />
        </div>
        <div className="padding__bottom--large">
            <CardArray
                name="message"
                title="Carousel Slide"
                cardClassName="sds_carousel-details__card"
                deleteDialogBodyText="Are you sure you want to delete this Slide?"
                newArrayValue={{}}
            >
                {(slideCardProps) => (
                    <>
                        <FileUploadFormik
                            id={`${slideCardProps.name}.image`}
                            label="Image"
                            helperText="One image must be uploaded per slide."
                            required
                            accept="image/*"
                            maxAllowedFiles={1}
                            onAddFiles={(newFiles) =>
                                formik.setFieldValue('images', [
                                    ...new Set([...(formik.values.images ?? []), ...newFiles]),
                                ])
                            }
                            onRemoveFile={(filename) =>
                                formik.setFieldValue(
                                    'images',
                                    formik.values.images?.filter(
                                        (img) =>
                                            img.name !== filename ||
                                            // Make sure no other slides are using the same image
                                            formik.values.message?.some(
                                                (slide, i) =>
                                                    i !== slideCardProps.index &&
                                                    slide.image?.[0]?.name === filename,
                                            ),
                                    ),
                                )
                            }
                            onFileClick={(filename) => {
                                saveAs(
                                    formik.values.images?.find((img) => img.name === filename),
                                    filename,
                                );
                            }}
                        />
                        <TextInputFormik
                            id={`${slideCardProps.name}.title`}
                            label="Title"
                            required
                            disabled={slideCardProps.readOnly}
                        />
                        <TextAreaFormik
                            id={`${slideCardProps.name}.message`}
                            label="Message"
                            required
                            disabled={slideCardProps.readOnly}
                        />
                    </>
                )}
            </CardArray>
        </div>
    </>
);

const previewContent = (values) => (
    <Carousel
        isOpen
        usePortal={false}
        slides={values.message?.map((slide) => ({
            ...slide,
            image: (() => {
                const imgForSlide = values.images?.find(
                    (img) => img.name === slide.image?.[0]?.name,
                );
                return imgForSlide && URL.createObjectURL(imgForSlide);
            })(),
        }))}
    />
);

export const CarouselDetails = () => (
    <HomeScreenMessageForm
        formTitle="Carousel Announcement"
        listPageUrl="/feature-campaign/carousel"
        formatInitialValues={(message) => ({
            ...MessageCategoryInput.initialValues(message),
            ...ApplicationInputs.initialValues(message),
            ...PublishDateInput.initialValues(message),
            ...EndDateInput.initialValues(message),
            ...DisplayInput.initialValues(message),
            message: message?.message ?? [{}],
            images: message?.images, // Used to hold the image File objects
        })}
        validationSchema={Yup.object({
            ...MessageCategoryInput.validationSchema,
            ...ApplicationInputs.validationSchema,
            ...PublishDateInput.validationSchema,
            ...EndDateInput.validationSchema,
            ...DisplayInput.validationSchema,
            message: Yup.array(
                Yup.object({
                    image: Yup.array().length(1, validationStrings.requiredField.errorMessage),
                    title: Yup.string().required(validationStrings.requiredField.errorMessage),
                    message: Yup.string().required(validationStrings.requiredField.errorMessage),
                }),
            ).min(1, validationStrings.requiredField.errorMessage),
        })}
        formatSubmitBody={(values) => ({
            message_type: HomeScreen.MessageType.CAROUSEL,
            ...MessageCategoryInput.submitValues(values),
            ...ApplicationInputs.submitValues(values),
            ...PublishDateInput.submitValues(values),
            ...EndDateInput.submitValues(values),
            ...DisplayInput.submitValues(values),
            message: values.message?.map((slide) => ({
                ...slide,
                // Only keep name & size props for the image
                image: slide.image?.map(({ name, size }) => ({ name, size })),
            })),
        })}
        formContent={formContent}
        previewContent={previewContent}
    />
);
